import React from "react";
import { Route, Navigate } from "react-router";
import { RequireAdminRoute } from "@/components/auth/RequireAdminRoute";
import { RouteGuard } from "@/components/auth/RouteGuard";
import Dashboard from "@/pages/Dashboard";
import ChildrenPage from "@/pages/Children";
import Employees from "@/pages/Employees";
import Contacts from "@/pages/Contacts";
import { ActivitiesListPage } from "@/pages/Activities/ActivitiesListPage";
import { billingRoutes } from "@/pages/billing/routes";
import ContractsTariffsPage from "@/pages/ContractsTariffsPage";
import { ContractErrorBoundary } from "@/components/billing/contracts/ContractErrorBoundary";
import ShiftAbsencesPage from "@/pages/ShiftAbsencesPage";
import { DocumentsPage } from "@/pages/Documents";
import SettlingInPage from "@/pages/SettlingIn";
import { SettlingInDetailWrapper } from "@/pages/settling-in/SettlingInDetailWrapper";
import WorkflowInboxPage from "@/pages/workflows/WorkflowInboxPage";
import { NotFoundPage } from "@/pages/NotFoundPage";

// Heavy, self-contained page routes are code-split so they don't weigh down the
// initial bundle (the SPA boots into the Dashboard, not these). Suspense in
// AppLayout covers the load fallback.
const ChildConsentsOverview = React.lazy(() => import("@/pages/children/ChildConsentsOverview"));
const ContractEditor = React.lazy(() => import("@/pages/billing/ContractEditor"));
const ContractTerminationReview = React.lazy(() => import("@/pages/contracts/ContractTerminationReview"));
const CalendarPage = React.lazy(() => import("@/pages/calendar/CalendarPage"));
const CapacityManagementUnified = React.lazy(() =>
  import("@/pages/CapacityManagementUnified").then((m) => ({
    default: m.CapacityManagementUnified,
  })),
);
const GraduationWorklist = React.lazy(() =>
  import("@/pages/capacity/GraduationWorklist").then((m) => ({
    default: m.GraduationWorklist,
  })),
);
const MessagingPage = React.lazy(() => import("@/pages/MessagingPage"));
// CRM prospects (issue #315). Module routes (`prospects`) since #1670.
const ProspectsPage = React.lazy(() =>
  import("@/pages/prospects").then((m) => ({ default: m.ProspectsPage })),
);
const ProspectCreatePage = React.lazy(() =>
  import("@/pages/prospects").then((m) => ({ default: m.ProspectCreatePage })),
);
const ProspectDetailWrapper = React.lazy(() =>
  import("@/pages/prospects").then((m) => ({ default: m.ProspectDetailWrapper })),
);
// The board imports its page module DIRECTLY rather than through the prospects
// barrel, so the barrel stays free of merge conflicts with #320. Same shape as
// ProspectEmailTemplateSettings below.
const ProspectBoardPage = React.lazy(
  () => import("@/pages/prospects/board/ProspectBoardPage"),
);
// The KPI page imports its page module DIRECTLY rather than through the prospects
// barrel, so the barrel stays free of merge conflicts with #1461.
const ProspectKpiPage = React.lazy(
  () => import("@/pages/prospects/kpis/ProspectKpiPage"),
);
const CoursesPage = React.lazy(() =>
  import("@/pages/Courses").then((m) => ({ default: m.CoursesPage })),
);
const TimeTrackingWorkforcePage = React.lazy(
  () => import("@/pages/TimeTrackingWorkforcePage"),
);
const KitchenViewPage = React.lazy(() =>
  import("@/pages/kitchen/KitchenViewPage").then((m) => ({
    default: m.KitchenViewPage,
  })),
);
const MenuActivitiesPage = React.lazy(() => import("@/pages/MenuActivitiesPage"));
const PortfolioWorkspacePage = React.lazy(() => import("@/pages/portfolio/PortfolioWorkspacePage"));

// Create/edit form routes -- code-split so the form-heavy bundles (and the
// react-pdf chain they reach through ImageUpload) load only when a form opens.
const CreateContactForm = React.lazy(() =>
  import("@/pages/contacts/CreateContactForm").then((m) => ({ default: m.CreateContactForm })),
);
const EditContactForm = React.lazy(() =>
  import("@/pages/contacts/EditContactForm").then((m) => ({ default: m.EditContactForm })),
);
const CreateEmployeeForm = React.lazy(() =>
  import("@/components/form/CreateEmployeeForm").then((m) => ({ default: m.CreateEmployeeForm })),
);
const EditEmployeeWrapper = React.lazy(() => import("@/components/form/EditEmployeeWrapper"));
const CreateChildrenForm = React.lazy(() =>
  import("@/pages/children/CreateChildrenForm").then((m) => ({ default: m.CreateChildrenForm })),
);
const EditChildrenWrapper = React.lazy(() => import("@/pages/children/EditChildrenWrapper"));

// Settings cluster -- ~20 sub-pages behind /settings/*, none on the boot path.
// Each is code-split so the settings area is fetched only when navigated to.
const SettingsLayout = React.lazy(() => import("@/pages/settings/SettingsLayout"));
const OrganisationSettings = React.lazy(() => import("@/pages/settings/OrganisationSettings"));
const ChildcareSettings = React.lazy(() => import("@/pages/settings/ChildcareSettings"));
const CustomizationSettings = React.lazy(() => import("@/pages/settings/CustomizationSettings"));
const GroupSettings = React.lazy(() => import("@/pages/settings/GroupSettings"));
const AccessRolesSettings = React.lazy(() => import("@/pages/settings/AccessRolesSettings"));
const DevicesSettings = React.lazy(() => import("@/pages/settings/DevicesSettings"));
const LabelSettings = React.lazy(() => import("@/pages/settings/LabelSettings"));
const AuditHistorySettings = React.lazy(() => import("@/pages/settings/AuditHistorySettings"));
const MessagingSettings = React.lazy(() => import("@/pages/settings/MessagingSettings"));
const BillingSettings = React.lazy(() => import("@/pages/settings/BillingSettings"));
const DunningSettings = React.lazy(() => import("@/pages/settings/DunningSettings"));
const ChangeRequestSettings = React.lazy(() => import("@/pages/settings/ChangeRequestSettings"));
const WorkflowDeadlineSettings = React.lazy(() => import("@/pages/settings/WorkflowDeadlineSettings"));
const CapacitySettings = React.lazy(() => import("@/pages/settings/CapacitySettings"));
const MatchingRulesSettings = React.lazy(() => import("@/components/billing/settings/MatchingRulesSettings"));
const SettlingInSettings = React.lazy(() => import("@/pages/settings/SettlingInSettings"));
const PortfolioGeneralSettings = React.lazy(() => import("@/pages/settings/PortfolioGeneralSettings"));
const PortfolioTaxonomySettings = React.lazy(() => import("@/pages/settings/PortfolioTaxonomySettings"));
const PortfolioCadenceSettings = React.lazy(() => import("@/pages/settings/PortfolioCadenceSettings"));
const HabitsSettings = React.lazy(() => import("@/pages/settings/HabitsSettings"));
const SubsidyOfficesPage = React.lazy(() => import("@/pages/settings/SubsidyOfficesPage"));
const SubsidyRatesSettings = React.lazy(() => import("@/pages/settings/SubsidyRatesPage"));
const KindergartenDatesPage = React.lazy(() => import("@/pages/settings/KindergartenDatesPage"));
const VacationEntitlementSchedulePage = React.lazy(() => import("@/pages/settings/VacationEntitlementSchedulePage"));
const PublicHolidaysPage = React.lazy(() => import("@/pages/settings/PublicHolidaysPage"));
const ShiftTemplateSettings = React.lazy(() => import("@/pages/settings/ShiftTemplateSettings"));
const AbsenceSettings = React.lazy(() => import("@/pages/settings/AbsenceSettings"));
const ShiftSettings = React.lazy(() => import("@/pages/settings/ShiftSettings"));
const ProspectEmailTemplateSettings = React.lazy(() => import("@/pages/settings/ProspectEmailTemplateSettings"));
const ProspectIntakeSettings = React.lazy(() => import("@/pages/settings/ProspectIntakeSettings"));
const GuidePage = React.lazy(() => import("@/pages/guide/GuidePage"));
const WhatsNewPage = React.lazy(() => import("@/pages/whats-new/WhatsNewPage"));
const GroupPresencePage = React.lazy(() => import("@/pages/team/GroupPresencePage"));

/**
 * The route table of the app shell. Every top-level route is wrapped in a
 * `RouteGuard` whose `path` equals the route path; `ROUTE_MODULES` in
 * `lib/modules.ts` maps each path to a module or to `admin` (issue #1670).
 * `appRoutes.test.tsx` fails when a route and the map drift apart.
 */
export const appRoutes = (
  <>
    <Route path="/" element={<RouteGuard path="/"><Dashboard /></RouteGuard>} />
    {/* Owner-only analytics surface (issue #906): a non-admin is redirected to the
        Overview dashboard instead of the cockpit-powered Analytics tab. */}
    <Route
      path="/dashboard/analytics"
      element={
        <RouteGuard path="/dashboard/analytics">
          <RequireAdminRoute fallback="/">
            <Dashboard />
          </RequireAdminRoute>
        </RouteGuard>
      }
    />
    {/* CRM prospects (issue #315, board #322). Module routes (`prospects`) since
        #1670. React Router ranks a static segment above a dynamic one, so
        "/prospects/new" and "/prospects/board" resolve to their own pages rather
        than to the ":id" detail. */}
    <Route path="/prospects" element={<RouteGuard path="/prospects"><ProspectsPage /></RouteGuard>} />
    <Route path="/prospects/new" element={<RouteGuard path="/prospects/new"><ProspectCreatePage /></RouteGuard>} />
    <Route path="/prospects/board" element={<RouteGuard path="/prospects/board"><ProspectBoardPage /></RouteGuard>} />
    <Route path="/prospects/kpis" element={<RouteGuard path="/prospects/kpis"><ProspectKpiPage /></RouteGuard>} />
    <Route path="/prospects/:id" element={<RouteGuard path="/prospects/:id"><ProspectDetailWrapper /></RouteGuard>} />
    <Route path="/children" element={<RouteGuard path="/children"><ChildrenPage /></RouteGuard>} />
    <Route path="/children/new" element={<RouteGuard path="/children/new"><CreateChildrenForm /></RouteGuard>} />
    {/* Org-wide consent overview (#1919), admin only. */}
    <Route path="/children/consents" element={<RouteGuard path="/children/consents"><ChildConsentsOverview /></RouteGuard>} />
    <Route path="/children/:id/edit" element={<RouteGuard path="/children/:id/edit"><EditChildrenWrapper /></RouteGuard>} />
    <Route path="/employees" element={<RouteGuard path="/employees"><Employees /></RouteGuard>} />
    <Route path="/employees/create" element={<RouteGuard path="/employees/create"><CreateEmployeeForm /></RouteGuard>} />
    <Route path="/employees/:id/edit" element={<RouteGuard path="/employees/:id/edit"><EditEmployeeWrapper /></RouteGuard>} />
    {/* Contacts (Families & Contacts) */}
    <Route path="/contacts" element={<RouteGuard path="/contacts"><Contacts /></RouteGuard>} />
    <Route path="/contacts/create" element={<RouteGuard path="/contacts/create"><CreateContactForm /></RouteGuard>} />
    <Route path="/contacts/:id/edit" element={<RouteGuard path="/contacts/:id/edit"><EditContactForm /></RouteGuard>} />

    {/* Enrolment & Wait-list is not built. Same treatment as
        /inspection: no nav entry, route kept as a redirect. */}
    <Route path="/enrolment" element={<RouteGuard path="/enrolment"><Navigate to="/" replace /></RouteGuard>} />

    {/* Operations */}
    <Route path="/capacity" element={<RouteGuard path="/capacity"><CapacityManagementUnified /></RouteGuard>} />
    <Route path="/capacity/graduation" element={<RouteGuard path="/capacity/graduation"><GraduationWorklist /></RouteGuard>} />
    <Route path="/activities" element={<RouteGuard path="/activities"><ActivitiesListPage /></RouteGuard>} />
    <Route path="/shifts" element={<RouteGuard path="/shifts"><ShiftAbsencesPage /></RouteGuard>} />
    {/* #1156: this rendered <CoverageDashboardPage /> with none of its six
        required props. The page that owns that state is /shifts, which
        selects the coverage tab from `?tab=`. */}
    <Route
      path="/shifts/coverage"
      element={<RouteGuard path="/shifts/coverage"><Navigate to="/shifts?tab=coverage" replace /></RouteGuard>}
    />
    <Route path="/time-tracking" element={<RouteGuard path="/time-tracking"><TimeTrackingWorkforcePage /></RouteGuard>} />
    {/* Group presence proof (#2114): Team > Gruppenpräsenz. */}
    <Route path="/team/presence" element={<RouteGuard path="/team/presence"><GroupPresencePage /></RouteGuard>} />
    <Route path="/workforce" element={<RouteGuard path="/workforce"><Navigate to="/time-tracking?tab=workforce" replace /></RouteGuard>} />
    <Route path="/messaging" element={<RouteGuard path="/messaging"><MessagingPage /></RouteGuard>} />
    <Route path="/courses" element={<RouteGuard path="/courses"><CoursesPage /></RouteGuard>} />
    <Route path="/kitchen" element={<RouteGuard path="/kitchen"><KitchenViewPage /></RouteGuard>} />
    <Route path="/documents" element={<RouteGuard path="/documents"><DocumentsPage /></RouteGuard>} />
    <Route path="/menu-activities" element={<RouteGuard path="/menu-activities"><MenuActivitiesPage /></RouteGuard>} />
    <Route path="/settling-in" element={<RouteGuard path="/settling-in"><SettlingInPage /></RouteGuard>} />
    <Route path="/settling-in/:id" element={<RouteGuard path="/settling-in/:id"><SettlingInDetailWrapper /></RouteGuard>} />
    {/* The cross-child Portfolio workspace (issue #1078) is a module route
        (`portfolio`) since #1670; its ageing-drafts section stays admin-gated
        in-handler. */}
    <Route path="/portfolio" element={<RouteGuard path="/portfolio"><PortfolioWorkspacePage /></RouteGuard>} />
    <Route path="/workflows" element={<RouteGuard path="/workflows"><WorkflowInboxPage /></RouteGuard>} />
    <Route path="/menus" element={<RouteGuard path="/menus"><Navigate to="/menu-activities?tab=menus" replace /></RouteGuard>} />
    <Route
      path="/weekly-activities"
      element={<RouteGuard path="/weekly-activities"><Navigate to="/menu-activities?tab=activities" replace /></RouteGuard>}
    />

    {/* Finance */}
    <Route path="/contracts" element={<RouteGuard path="/contracts"><ContractsTariffsPage /></RouteGuard>} />
    <Route path="/contracts/:id/edit" element={
      <RouteGuard path="/contracts/:id/edit">
        <ContractErrorBoundary>
          <ContractEditor />
        </ContractErrorBoundary>
      </RouteGuard>
    } />
    <Route path="/contract-terminations/:id/review" element={
      <RouteGuard path="/contract-terminations/:id/review">
        <ContractErrorBoundary>
          <ContractTerminationReview />
        </ContractErrorBoundary>
      </RouteGuard>
    } />

    {/* Compliance & Reports has no implementation behind it yet (Epic F,
        inspection export). The nav entry is gone; the routes stay as
        redirects so existing deep-links and bookmarks land somewhere
        usable instead of on an empty page. */}
    <Route path="/inspection" element={<RouteGuard path="/inspection"><Navigate to="/" replace /></RouteGuard>} />
    <Route path="/analytics" element={<RouteGuard path="/analytics"><Navigate to="/" replace /></RouteGuard>} />

    {/* Billing Routes (the `billing` route carries its own RouteGuard) */}
    {billingRoutes}

    {/* Admin path kept as a redirect for old deep-links. */}
    <Route
      path="/admin/subsidy-offices"
      element={<RouteGuard path="/admin/subsidy-offices"><Navigate to="/settings/subsidy-offices" replace /></RouteGuard>}
    />

    {/* Settings: every page is admin-only through the `/settings` rule in ROUTE_MODULES. */}
    <Route path="/settings" element={<RouteGuard path="/settings"><SettingsLayout /></RouteGuard>}>
      <Route index element={<Navigate to="/settings/organisation" replace />} />
      <Route path="organisation" element={<OrganisationSettings />} />
      <Route path="childcare" element={<ChildcareSettings />} />
      <Route path="groups" element={<GroupSettings />} />
      <Route path="access-roles" element={<AccessRolesSettings />} />
      <Route path="devices" element={<DevicesSettings />} />
      <Route path="capacity" element={<CapacitySettings />} />
      <Route path="finance" element={<BillingSettings />} />
      <Route path="dunning" element={<DunningSettings />} />
      <Route path="prospect-email-templates" element={<ProspectEmailTemplateSettings />} />
      <Route path="prospect-intake" element={<ProspectIntakeSettings />} />
      <Route path="change-requests" element={<ChangeRequestSettings />} />
      <Route path="workflow-deadlines" element={<WorkflowDeadlineSettings />} />
      <Route path="matching-rules" element={<MatchingRulesSettings />} />
      <Route path="labels" element={<LabelSettings />} />
      <Route path="customization" element={<CustomizationSettings />} />
      <Route path="messaging" element={<MessagingSettings />} />
      <Route path="habits" element={<HabitsSettings />} />
      <Route path="settling-in" element={<SettlingInSettings />} />
      <Route path="portfolio" element={<PortfolioGeneralSettings />} />
      <Route path="portfolio-taxonomy" element={<PortfolioTaxonomySettings />} />
      <Route path="portfolio-cadence" element={<PortfolioCadenceSettings />} />
      <Route path="subsidy-offices" element={<SubsidyOfficesPage />} />
      <Route path="subsidy-rates" element={<SubsidyRatesSettings />} />
      <Route path="kindergarten-dates" element={<KindergartenDatesPage />} />
      <Route path="vacation-entitlement-schedule" element={<VacationEntitlementSchedulePage />} />
      <Route path="public-holidays" element={<PublicHolidaysPage />} />
      <Route path="shift-templates" element={<ShiftTemplateSettings />} />
      <Route path="shift-planning" element={<ShiftSettings />} />
      <Route path="absences" element={<AbsenceSettings />} />
      <Route path="audit" element={<AuditHistorySettings />} />
    </Route>

    <Route path="/calendar" element={<RouteGuard path="/calendar"><CalendarPage /></RouteGuard>} />

    {/* The platform guide (#2059): one page that reads its view from the URL. */}
    <Route path="/guide" element={<RouteGuard path="/guide"><GuidePage /></RouteGuard>}>
      <Route path="glossary" element={null} />
      <Route path="faq" element={null} />
      <Route path=":topicId" element={null} />
    </Route>
    {/* What's new (#2109): the release notes, built with the guide. */}
    <Route path="/whats-new" element={<RouteGuard path="/whats-new"><WhatsNewPage /></RouteGuard>} />
    {/* An unknown address says so instead of silently showing the dashboard. */}
    <Route path="*" element={<NotFoundPage />} />
  </>
);
